import React from 'react';
import { completionColor } from '../../forms/utils';
import type { FormListFilter, FormListItem } from '../../forms/types';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Input } from '../Input/Input';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { Segmented } from '../Segmented/Segmented';
import { FormsComplianceBadge } from './FormsComplianceBadge';
import { FormsStatusPill } from './FormsStatusPill';

const FILTER_OPTIONS: { value: FormListFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'archived', label: 'Archived' },
];

export interface FormsListPanelProps {
  forms: FormListItem[];
  /** Total forms before search/filter — used for empty-state copy. */
  totalCount?: number;
  filter: FormListFilter;
  onFilterChange: (filter: FormListFilter) => void;
  counts: Record<FormListFilter, number>;
  search: string;
  onSearchChange: (search: string) => void;
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onEdit?: (id: string) => void;
  onSend?: (form: FormListItem) => void;
  onDelete?: (id: string) => void;
  onArchive?: (ids: string[]) => void;
  onBulkDelete?: (ids: string[]) => void;
  onCreate?: () => void;
}

export function FormsListPanel({
  forms,
  totalCount = forms.length,
  filter,
  onFilterChange,
  counts,
  search,
  onSearchChange,
  selected,
  onToggleSelect,
  onToggleAll,
  onEdit,
  onSend,
  onDelete,
  onArchive,
  onBulkDelete,
  onCreate,
}: FormsListPanelProps) {
  const allSelected = selected.size === forms.length && forms.length > 0;
  const isLibraryEmpty = totalCount === 0;
  const isFilteredEmpty = !isLibraryEmpty && forms.length === 0;

  return (
    <div className="fm-table-outer">
      <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
        <div className="w-full max-w-[320px] flex-[0_1_320px]">
          <Input
            icon="search"
            placeholder="Search forms…"
            value={search}
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
          />
        </div>
        <Segmented
          value={filter === 'compliance' ? 'all' : filter}
          onChange={(v) => {
            onFilterChange(v as FormListFilter);
          }}
          options={FILTER_OPTIONS.map((opt) => ({
            value: opt.value,
            label: (
              <span className="inline-flex items-center gap-1.5">
                {opt.label}
                <span className="font-[var(--font-mono)] text-[11px] opacity-70">{counts[opt.value]}</span>
              </span>
            ),
          }))}
        />
        <button
          type="button"
          onClick={() => {
            onFilterChange('compliance');
          }}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[var(--r-pill)] border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition',
            filter === 'compliance'
              ? 'border-[var(--gold)] bg-[var(--gold-tint)] text-[#9A6B1E]'
              : 'border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--gold)]',
          )}
        >
          <Icon kind="shield-check" size={12} stroke={2} />
          Compliance
          <span className="font-[var(--font-mono)] text-[10px] opacity-70">{counts.compliance}</span>
        </button>
        {selected.size > 0 ? (
          <div className="ml-auto flex animate-in items-center gap-2 fade-in duration-150">
            <span className="text-[12.5px] text-[var(--ink-3)]">{selected.size} selected</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onArchive?.([...selected]);
              }}
            >
              Archive
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                onBulkDelete?.([...selected]);
              }}
            >
              Delete
            </Button>
          </div>
        ) : null}
      </div>

      <div className="fm-table-wrap">
        <div className="fm-table-scroll">
          <div className="fm-table-inner">
            <div className="fm-table-head">
              <Checkbox checked={allSelected} onChange={onToggleAll} />
              <div>Form</div>
              <div>Trigger</div>
              <div>Status</div>
              <div>Submitted</div>
              <div>Completion</div>
              <div className="text-right">Actions</div>
            </div>
            {forms.length === 0 ? (
              <div className="flex flex-col items-center px-5 py-16 text-center">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-[var(--r-lg)] bg-[var(--cream-0)] text-[var(--ink-3)]">
                  <Icon kind="clipboard" size={22} stroke={1.8} />
                </div>
                {isLibraryEmpty ? (
                  <>
                    <p className="text-[15px] font-medium text-[var(--ink-0)]">No forms yet</p>
                    <p className="mt-1.5 max-w-[360px] text-[13px] leading-relaxed text-[var(--ink-3)]">
                      Create your first intake, consent, or policy form. ELVA can send it automatically and track
                      completion for every patient.
                    </p>
                    {onCreate ? (
                      <Button
                        variant="primary"
                        size="sm"
                        className="mt-5"
                        onClick={onCreate}
                      >
                        Create form
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <p className="text-[15px] font-medium text-[var(--ink-0)]">No forms match this view</p>
                    <p className="mt-1.5 max-w-[360px] text-[13px] leading-relaxed text-[var(--ink-3)]">
                      {isFilteredEmpty && search.trim()
                        ? `Nothing matched "${search.trim()}". Try a different search or filter.`
                        : 'Try clearing the search or selecting a different filter.'}
                    </p>
                    {(search.trim() || filter !== 'all') && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="mt-5"
                        onClick={() => {
                          onSearchChange('');
                          onFilterChange('all');
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              forms.map((form) => (
                <div key={form.id} className="fm-table-row group">
              <Checkbox
                checked={selected.has(form.id)}
                onChange={() => {
                  onToggleSelect(form.id);
                }}
              />
              <div className="min-w-0 pr-5">
                <div className="mb-0.5 flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-[var(--ink-0)]">{form.name}</span>
                  {form.compliance ? <FormsComplianceBadge /> : null}
                </div>
                <p className="truncate text-xs text-[var(--ink-3)]">{form.desc}</p>
                <p className="mt-1 font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
                  {form.blocks} {form.blocks === 1 ? 'block' : 'blocks'} · last sent {form.lastSent}
                </p>
              </div>
              <div className="flex items-start gap-1.5 pr-3">
                {form.auto ? <Icon kind="bolt" size={12} className="text-[var(--gold)]" /> : null}
                <span
                  className={cn(
                    'text-[12.5px] leading-snug',
                    form.trigger === 'Not configured' ? 'italic text-[var(--ink-4)]' : 'text-[var(--ink-2)]',
                  )}
                >
                  {form.trigger}
                </span>
              </div>
              <FormsStatusPill status={form.status} />
              <div>
                <p className="font-[var(--font-mono)] text-sm font-medium text-[var(--ink-0)]">{form.submitted}</p>
                <p className="mt-0.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">of {form.sent} sent</p>
              </div>
              <div className="flex items-center gap-2">
                {form.sent > 0 ? (
                  <>
                    <div className="h-1 max-w-[50px] flex-1">
                      <ProgressBar value={form.completion} />
                    </div>
                    <span
                      className="font-[var(--font-mono)] text-xs font-semibold"
                      style={{ color: completionColor(form.completion) }}
                    >
                      {form.completion}%
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-[var(--ink-4)]">—</span>
                )}
              </div>
              <div className="flex items-center justify-end gap-1 opacity-55 transition-opacity group-hover:opacity-100">
                <IconButton
                  icon="send"
                  size={30}
                  label="Send to patient"
                  disabled={form.status !== 'published'}
                  onClick={() => {
                    onSend?.(form);
                  }}
                  className="!rounded-[7px] text-[var(--success)]"
                />
                <IconButton
                  icon="pencil"
                  size={30}
                  label="Edit form"
                  onClick={() => {
                    onEdit?.(form.id);
                  }}
                  className="!rounded-[7px]"
                />
                <IconButton icon="copy" size={30} label="Duplicate" className="!rounded-[7px]" />
                <IconButton
                  icon={form.compliance ? 'lock' : 'trash'}
                  size={30}
                  label={form.compliance ? 'Compliance Pack forms cannot be deleted' : 'Delete'}
                  disabled={form.compliance}
                  onClick={() => {
                    onDelete?.(form.id);
                  }}
                  className="!rounded-[7px]"
                />
                <IconButton icon="dots" size={30} label="More" className="!rounded-[7px]" />
              </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex shrink-0 items-center justify-between px-1">
        <p className="font-[var(--font-mono)] text-xs text-[var(--ink-4)]">
          {forms.length} forms shown
        </p>
        <p className="flex items-center gap-1.5 text-xs text-[var(--ink-4)]">
          <Icon kind="shield" size={12} className="text-[var(--gold)]" />
          Compliance Pack forms are auto-installed and cannot be deleted.
        </p>
      </div>
    </div>
  );
}
